import React, { useMemo } from 'react';
import {
  Pressable,
  type PressableStateCallbackType,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import type { BottomTabBarProps } from '@react-navigation/bottom-tabs';
import { CommonActions } from '@react-navigation/native';
import { getAppInfo } from '../config/appInfo';
import { layout } from '../constants/layout';
import { spacing } from '../constants/spacing';
import { typography } from '../constants/typography';
import { useAuth } from '../context/AuthContext';
import { useAppColors } from '../context/PreferencesContext';
import { toCamelCaseLabel } from '../utils/camelCaseLabel';
import { webPointer } from '../utils/webLayout';
import { ProfileAvatar } from './ProfileAvatar';

type Props = BottomTabBarProps & {
  /** rail: icon-over-label column for tablets; expanded: labelled sidebar. */
  mode: 'rail' | 'expanded';
};

/** react-native-web adds `hovered` to the Pressable state. */
type WebPressState = PressableStateCallbackType & { hovered?: boolean };

/**
 * Web-only replacement for the bottom tab bar from tablet width up.
 * Renders the same tab routes, icons, and badges as the mobile tab bar.
 */
export function WebSidebar({ state, descriptors, navigation, mode }: Props) {
  const palette = useAppColors();
  const auth = useAuth();
  const appName = useMemo(() => getAppInfo().appName, []);
  const expanded = mode === 'expanded';
  const userName =
    auth.user?.displayName || auth.user?.username || 'Signed-in user';

  return (
    <View
      role="navigation"
      style={[
        styles.root,
        {
          width: expanded ? layout.webSidebarWidth : layout.webSidebarRailWidth,
          backgroundColor: palette.surface,
          borderRightColor: palette.border,
        },
      ]}
    >
      <View style={[styles.brand, expanded ? null : styles.brandRail]}>
        <View style={[styles.brandMark, { backgroundColor: palette.brand }]}>
          <Text style={[styles.brandMarkText, { color: palette.white }]}>
            {appName.trim().slice(0, 1).toUpperCase()}
          </Text>
        </View>
        {expanded ? (
          <Text
            numberOfLines={1}
            style={[styles.brandName, { color: palette.textPrimary }]}
          >
            {appName}
          </Text>
        ) : null}
      </View>

      <ScrollView
        style={styles.items}
        contentContainerStyle={[
          styles.itemsContent,
          expanded ? null : styles.itemsContentRail,
        ]}
        showsVerticalScrollIndicator={false}
      >
        {state.routes.map((route, index) => {
          const { options } = descriptors[route.key];
          const focused = state.index === index;
          const color = focused ? palette.primary : palette.textSecondary;
          const rawLabel =
            typeof options.tabBarLabel === 'string'
              ? options.tabBarLabel
              : options.title ?? route.name;
          const label = toCamelCaseLabel(rawLabel);
          const badge = options.tabBarBadge;

          const onPress = () => {
            const event = navigation.emit({
              type: 'tabPress',
              target: route.key,
              canPreventDefault: true,
            });
            if (!focused && !event.defaultPrevented) {
              navigation.dispatch({
                ...CommonActions.navigate(route.name, route.params),
                target: state.key,
              });
            }
          };

          return (
            <Pressable
              key={route.key}
              accessibilityRole="tab"
              accessibilityLabel={options.tabBarAccessibilityLabel ?? label}
              accessibilityState={{ selected: focused }}
              testID={options.tabBarButtonTestID}
              onPress={onPress}
              onLongPress={() =>
                navigation.emit({ type: 'tabLongPress', target: route.key })
              }
              style={state_ => {
                const { hovered, pressed } = state_ as WebPressState;
                return [
                  styles.item,
                  expanded ? styles.itemExpanded : styles.itemRail,
                  webPointer,
                  focused || hovered || pressed
                    ? { backgroundColor: palette.background }
                    : null,
                ];
              }}
            >
              {focused && expanded ? (
                <View
                  style={[styles.activeBar, { backgroundColor: palette.primary }]}
                />
              ) : null}
              <View style={styles.iconWrap}>
                {options.tabBarIcon?.({ focused, color, size: 22 })}
                {badge != null && !expanded ? (
                  <View style={[styles.badgeDot, { backgroundColor: palette.error }]}>
                    <Text style={[styles.badgeText, { color: palette.onError }]}>
                      {badge}
                    </Text>
                  </View>
                ) : null}
              </View>
              <Text
                numberOfLines={1}
                style={[
                  expanded ? styles.labelExpanded : styles.labelRail,
                  { color: focused ? palette.primary : palette.textSecondary },
                  focused ? styles.labelFocused : null,
                ]}
              >
                {label}
              </Text>
              {badge != null && expanded ? (
                <View style={[styles.badge, { backgroundColor: palette.error }]}>
                  <Text style={[styles.badgeText, { color: palette.onError }]}>
                    {badge}
                  </Text>
                </View>
              ) : null}
            </Pressable>
          );
        })}
      </ScrollView>

      <View
        style={[
          styles.footer,
          expanded ? null : styles.footerRail,
          { borderTopColor: palette.border },
        ]}
      >
        <ProfileAvatar name={userName} size={expanded ? 36 : 32} />
        {expanded ? (
          <Text
            numberOfLines={1}
            style={[styles.userName, { color: palette.textPrimary }]}
          >
            {userName}
          </Text>
        ) : null}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    height: '100%',
    borderRightWidth: 1,
    paddingTop: spacing.lg,
    flexShrink: 0,
  },
  brand: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    paddingHorizontal: spacing.lg,
    paddingBottom: spacing.lg,
  },
  brandRail: {
    justifyContent: 'center',
    paddingHorizontal: 0,
  },
  brandMark: {
    width: 36,
    height: 36,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
  },
  brandMarkText: {
    fontSize: 18,
    fontWeight: '800',
  },
  brandName: {
    ...typography.sectionTitle,
    fontWeight: '700',
    flexShrink: 1,
  },
  items: { flex: 1 },
  itemsContent: {
    paddingHorizontal: spacing.md,
    gap: spacing.xs,
  },
  itemsContentRail: {
    paddingHorizontal: spacing.sm,
  },
  item: {
    borderRadius: 10,
    alignItems: 'center',
    position: 'relative',
  },
  itemExpanded: {
    flexDirection: 'row',
    gap: spacing.md,
    minHeight: 44,
    paddingHorizontal: spacing.md,
  },
  itemRail: {
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.xs,
    gap: spacing.xs,
  },
  activeBar: {
    position: 'absolute',
    left: 0,
    top: 10,
    bottom: 10,
    width: 3,
    borderRadius: 2,
  },
  iconWrap: {
    width: 24,
    height: 24,
    alignItems: 'center',
    justifyContent: 'center',
  },
  labelExpanded: {
    ...typography.body,
    fontSize: 15,
    flex: 1,
    minWidth: 0,
  },
  labelRail: {
    fontSize: 11,
    lineHeight: 14,
    fontWeight: '600',
    textAlign: 'center',
  },
  labelFocused: { fontWeight: '700' },
  badge: {
    minWidth: 22,
    height: 20,
    paddingHorizontal: 6,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
  },
  badgeDot: {
    position: 'absolute',
    top: -6,
    right: -10,
    minWidth: 18,
    height: 18,
    paddingHorizontal: 4,
    borderRadius: 9,
    alignItems: 'center',
    justifyContent: 'center',
  },
  badgeText: {
    fontSize: 11,
    fontWeight: '700',
  },
  footer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    borderTopWidth: 1,
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.md,
  },
  footerRail: {
    justifyContent: 'center',
    paddingHorizontal: 0,
  },
  userName: {
    ...typography.body,
    fontSize: 14,
    fontWeight: '600',
    flex: 1,
    minWidth: 0,
  },
});
